import React, { useEffect, useState } from 'react';
import { app, authentication } from "@microsoft/teams-js";  // Teams SDK
import './App.css';

interface ListItem {
  id: string;
  title: string;
  company: string;
  hours: number;
  dueDate: string;
}

// Configuration: SharePoint site and list (replace with actual site and list name or GUIDs as needed)
const SHAREPOINT_SITE_PATH = "gruvrosse.sharepoint.com:/sites/teams-personal-tab";
const LIST_NAME = "WorkPlan";

const App: React.FC = () => {
  const [items, setItems] = useState<ListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [consentRequired, setConsentRequired] = useState<boolean>(false);

  useEffect(() => {
    // Initialize Teams SDK and get context
    app.initialize().then(() => {
      app.getContext().then((context) => {
        const userUPN = context.user?.userPrincipalName || "";  // user's email/UPN
        // Try to get an SSO token silently
        authentication.getAuthToken().then(async (ssoToken) => {
          console.log("SSO token acquired");
          await fetchListItems(ssoToken, userUPN);
        }).catch((err) => {
          console.error("SSO getAuthToken failed:", err);
          if (err === "resourceRequiresConsent" || err.errorCode === "resourceRequiresConsent") {
            // If additional consent is needed (first time use or new scope)
            setConsentRequired(true);
          } else {
            setError("SSO sign-in failed: " + err.toString());
          }
        });
      });
    });
  }, []);

  // Fetch list items from SharePoint via Microsoft Graph
  const fetchListItems = async (accessToken: string, userUPN: string) => {
    try {
      // Graph API endpoint to get list items and specific fields
      const endpoint = `https://graph.microsoft.com/v1.0/sites/${SHAREPOINT_SITE_PATH}:/lists/${LIST_NAME}/items?expand=fields(select=Title,Company,hours,dueDate,AssignedTo)`;
      const res = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (!res.ok) {
        if (res.status === 401) {
          // Likely the token is valid but missing consent for Graph scopes
          throw new Error("ConsentRequired");
        }
        throw new Error(`Graph API error: ${res.statusText}`);
      }
      const data = await res.json();
      console.log("Graph list items:", data);
      const allItems = data.value;
      // Filter items where AssignedTo contains the current user
      const currentUserEmail = userUPN.toLowerCase();
      const filteredItems = allItems.filter((item: any) => {
        const assigned = item.fields.AssignedTo;
        if (!assigned) return false;
        if (Array.isArray(assigned)) {
          // Multi-value person field returns an array with emails[3](https://stackoverflow.com/questions/54665408/using-graph-api-to-query-sharepoint-list-items-and-expand-user-field)
          return assigned.some((person: any) => person.email?.toLowerCase() === currentUserEmail);
        } else {
          // Single-value person field (in case the list allows only one assignee)
          return assigned.email?.toLowerCase() === currentUserEmail;
        }
      });
      // Map to our ListItem structure
      const myItems: ListItem[] = filteredItems.map((item: any) => ({
        id: item.id,
        title: item.fields.Title,
        company: item.fields.Company || "",
        hours: item.fields.hours || 0,
        dueDate: item.fields.dueDate || ""
      }));
      setItems(myItems);
    } catch (err: any) {
      if (err.message === "ConsentRequired") {
        setConsentRequired(true);
      } else {
        console.error(err);
        setError(err.message || "Error fetching list items");
      }
    }
  };

  // Trigger interactive auth popup to get Graph consent
  const requestConsent = () => {
    authentication.authenticate({
      url: window.location.origin + "/auth-start.html",
      width: 600,
      height: 535
    }).then((result) => {
      // result is the Graph access token from auth-start
      console.log("Consent popup success, Graph token acquired");
      fetchListItems(result as string, (app.getContext as any)?._context.user.userPrincipalName || "");
    }).catch((reason) => {
      console.error("Consent popup failed:", reason);
      setError("Unable to sign in or consent was not granted.");
    });
  };

  // Render logic
  if (error) {
    return <div className="error">Error: {error}</div>;
  }
  if (consentRequired) {
    return (
      <div className="consentPrompt">
        <p>We need your consent to access your tasks.</p>
        <button onClick={requestConsent}>Sign in and consent</button>
      </div>
    );
  }
  if (items === null) {
    return <div className="loading">Loading your tasks...</div>;
  }
  return (
    <div className="cardsContainer">
      {items.map(item => (
        <div key={item.id} className="card">
          <h3>{item.title}</h3>
          <p><strong>Company:</strong> {item.company}</p>
          <p><strong>Hours:</strong> {item.hours}</p>
          <p><strong>Due Date:</strong> {item.dueDate ? new Date(item.dueDate).toLocaleDateString() : ""}</p>
        </div>
      ))}
    </div>
  );
};

export default App;